/**
 * Réglages › Réservations — the org's bookable équipements: the list, a
 * template picker for a new one, and the policy form to edit or archive it.
 *
 * The layout route (`reglages.tsx`) owns the `canManage` gate for the whole
 * Réglages subtree (design-guidelines §11: one gate per subtree); this adds
 * only the module gate on top, same as the nav/tab entries that point here.
 */
import type { AmenityTemplate, AmenityUpsertInput } from "@gately/validators";
import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Archive, ChevronLeft, Plus } from "lucide-react";

import { Button } from "@gately/ui/button";
import { Card } from "@gately/ui/card";
import { ConfirmDialog } from "@gately/ui/confirm-dialog";
import {
  DetailSheet,
  DetailSheetBody,
  DetailSheetFooter,
  DetailSheetHeader,
} from "@gately/ui/detail-sheet";
import { EmptyState } from "@gately/ui/empty-state";
import { SettingsSection } from "@gately/ui/settings";
import { StatusBadge } from "@gately/ui/status-badge";
import { toast } from "@gately/ui/toast";
import {
  AMENITY_TEMPLATES,
  amenityTemplateLabel,
  bookingRuleMessage,
} from "@gately/validators";

import { TEMPLATE_ICONS } from "~/components/booking/amenity-icons";
import { AmenityForm } from "~/components/booking/AmenityForm";
import {
  formStateFromAmenity,
  formStateFromTemplate,
} from "~/lib/amenity-form";
import { ruleReason } from "~/lib/booking-format";
import { orpc } from "~/lib/orpc";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/reglages/reservations")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).hasAmenities) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  // Prefetch so useSuspenseQuery has data ready and never suspends mid-navigation.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(orpc.amenity.list.queryOptions()),
  component: ReservationsTab,
});

const TEMPLATE_BLURBS: Record<AmenityTemplate, string> = {
  meeting_room: "À l'heure, une réservation à la fois.",
  coworking: "Places partagées, à la demi-journée ou à la journée.",
  sport_court: "Créneaux fixes de 1 h 30, 7 jours sur 7.",
  gym: "Créneaux d'une heure, plusieurs personnes à la fois.",
  other: "Plage libre, chaque demande soumise à validation.",
};

/** The toast for a failed save: the engine's own sentence when the error
 *  carries a known reason, a generic line otherwise. */
function saveErrorMessage(e: unknown): string {
  const reason = ruleReason(e);
  return reason ? bookingRuleMessage(reason) : "Enregistrement impossible.";
}

function ReservationsTab() {
  const { data: amenities } = useSuspenseQuery(
    orpc.amenity.list.queryOptions(),
  );
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  return (
    <>
      <div className="mb-3 flex items-start justify-between gap-4">
        <SettingsSection
          title="Équipements réservables"
          hint="Salles, espaces et terrains que les occupants peuvent réserver."
        />
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          Nouvel équipement
        </Button>
      </div>

      {amenities.length === 0 ? (
        <Card>
          <EmptyState
            title="Aucun équipement."
            description="Créez le premier à partir d'un modèle : salle de réunion, coworking, terrain…"
          />
        </Card>
      ) : (
        <Card className="divide-y overflow-hidden">
          {amenities.map((a) => {
            const Icon = TEMPLATE_ICONS[a.template];
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => setEditId(a.id)}
                className="flex w-full items-center gap-3 p-4 text-left hover:bg-neutral-50"
              >
                <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
                  <Icon className="size-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{a.name}</span>
                  <span className="text-muted-foreground block truncate text-xs">
                    {[
                      a.locationLabel,
                      a.capacity === 1 ? "Privatif" : `${a.capacity} places`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="flex shrink-0 flex-wrap justify-end gap-1.5">
                  {a.approval === "required" ? (
                    <StatusBadge tone="info">Validation requise</StatusBadge>
                  ) : null}
                  {a.status === "archived" ? (
                    <StatusBadge tone="neutral">Archivé</StatusBadge>
                  ) : null}
                </span>
              </button>
            );
          })}
        </Card>
      )}

      {creating ? <CreateSheet onClose={() => setCreating(false)} /> : null}
      {editId ? (
        <EditSheet id={editId} onClose={() => setEditId(null)} />
      ) : null}
    </>
  );
}

/** Step 1 picks a template, step 2 is the form it seeds. « Changer de
 *  modèle » drops what was typed on step 2 — a different template means
 *  different defaults, and merging the two would be a guess. */
function CreateSheet({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [template, setTemplate] = useState<AmenityTemplate | null>(null);

  const create = useMutation(
    orpc.amenity.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Équipement créé");
        await queryClient.invalidateQueries({
          queryKey: orpc.amenity.list.key(),
        });
        onClose();
      },
      onError: (e) => toast.error(saveErrorMessage(e)),
    }),
  );

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width="lg"
    >
      <DetailSheetHeader
        title={template ? amenityTemplateLabel(template) : "Nouvel équipement"}
        meta={
          template ? (
            <button
              type="button"
              onClick={() => setTemplate(null)}
              className="hover:text-foreground inline-flex items-center gap-1"
            >
              <ChevronLeft className="size-3.5" />
              Changer de modèle
            </button>
          ) : (
            "Choisissez un modèle ; tout reste modifiable ensuite."
          )
        }
      />
      <DetailSheetBody>
        {template ? (
          <AmenityForm
            key={template}
            initial={formStateFromTemplate(template)}
            submitLabel="Créer l'équipement"
            pending={create.isPending}
            onSubmit={(input) => create.mutate(input)}
          />
        ) : (
          <div className="grid gap-2">
            {AMENITY_TEMPLATES.map((t) => {
              const Icon = TEMPLATE_ICONS[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTemplate(t)}
                  className="flex items-center gap-3 rounded-lg border p-4 text-left hover:bg-neutral-50"
                >
                  <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
                    <Icon className="size-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium">
                      {amenityTemplateLabel(t)}
                    </span>
                    <span className="text-muted-foreground block text-xs">
                      {TEMPLATE_BLURBS[t]}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </DetailSheetBody>
    </DetailSheet>
  );
}

function EditSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const terms = useTerms();
  const { data: amenity, isError } = useQuery(
    orpc.amenity.get.queryOptions({ input: { id } }),
  );
  const [conflictIds, setConflictIds] = useState<string[]>([]);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.amenity.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.amenity.get.key() }),
    ]);

  const update = useMutation(
    orpc.amenity.update.mutationOptions({
      onSuccess: async () => {
        setConflictIds([]);
        toast.success("Équipement enregistré");
        await refresh();
        onClose();
      },
      onError: (e) => {
        if (ruleReason(e) === "CAPACITY_CONFLICT") {
          const ids = (e as { data?: { bookingIds?: unknown } }).data
            ?.bookingIds;
          setConflictIds(
            Array.isArray(ids)
              ? ids.filter((x): x is string => typeof x === "string")
              : [],
          );
        }
        toast.error(saveErrorMessage(e));
      },
    }),
  );

  const archive = useMutation(
    orpc.amenity.archive.mutationOptions({
      onSuccess: async () => {
        setArchiveOpen(false);
        toast.success("Équipement archivé");
        await refresh();
        onClose();
      },
      onError: (e) => {
        setArchiveOpen(false);
        toast.error(saveErrorMessage(e));
      },
    }),
  );

  const state = amenity ? "ready" : isError ? "error" : "loading";

  function renderReady() {
    if (!amenity) return null;
    const archived = amenity.status === "archived";
    return (
      <>
        <DetailSheetHeader
          title={amenity.name}
          meta={
            <span className="inline-flex items-center gap-2">
              {amenityTemplateLabel(amenity.template)}
              {archived ? (
                <StatusBadge tone="neutral">Archivé</StatusBadge>
              ) : null}
            </span>
          }
        />
        <DetailSheetBody>
          <AmenityForm
            key={amenity.id}
            initial={formStateFromAmenity(amenity)}
            submitLabel="Enregistrer"
            pending={update.isPending}
            amenityId={amenity.id}
            conflictBookingIds={conflictIds}
            onSubmit={(data: AmenityUpsertInput) =>
              update.mutate({ id: amenity.id, data })
            }
          />
        </DetailSheetBody>
        {archived ? null : (
          <DetailSheetFooter destructive>
            <Button
              type="button"
              variant="outline"
              className="text-destructive"
              onClick={() => setArchiveOpen(true)}
            >
              <Archive className="size-4" />
              Archiver
            </Button>
          </DetailSheetFooter>
        )}
        <ConfirmDialog
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          title="Archiver cet équipement ?"
          description={
            <>
              <strong>{amenity.name}</strong> ne pourra plus être réservé par
              les occupants de {terms.theOrg}. Les réservations existantes
              restent valables.
            </>
          }
          confirmLabel="Archiver"
          pendingLabel="Archivage…"
          destructive
          pending={archive.isPending}
          onConfirm={() => archive.mutate({ id: amenity.id })}
        />
      </>
    );
  }

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width="lg"
      state={state}
      placeholderTitle="Équipement"
    >
      {renderReady()}
    </DetailSheet>
  );
}
